import * as React from 'react'
import { Carousel } from '@amzn/dvui/dist/Carousel'
import Header from './Header/Header'
import Card from './Card/Card'
import * as style from './FacetedCarousel.module.scss'
import * as cardStyle from './Card/Card.module.scss'
import * as csm from '../../../utils/csm'
import InView from 'react-intersection-observer'
import * as cn from 'classnames'
import { CollectionProps } from '../Collection';
import { StorefrontStateItem } from '../../../containers/Storefront';
import { safeBool } from 'DVWebNodeUtils'


interface FacetedCarouselProps extends CollectionProps {
  isIsolatedCarousel?: boolean
  isTwinHeroCarouselType?: boolean
  isSquareCarouselType?: boolean
  isPortraitCarouselType?: boolean
  isDoubleDeckerCarouselType?: boolean
  isBoxArtCarouselType?: boolean
}

interface FacetedCarouselState {
  items: StorefrontStateItem[]
  isEditActive: boolean
  isDirty: boolean
  truncateItemList: boolean
}

const CAROUSEL_THRESHOLD = 8
const DOUBLE_DECKER_CAROUSEL_THRESHOLD = 14
const PRELOADING_LOADING_CARDS_MULTIPLE = 2

class FacetedCarousel extends React.PureComponent<FacetedCarouselProps, FacetedCarouselState> {
  state: FacetedCarouselState
  isGettingEnriched: boolean
  isPartiallyEnriched: boolean
  isFullyEnriched: boolean
  containerRef: React.RefObject<HTMLDivElement>
  isHoriPagActive: boolean
  isHoriPageInitPreloadActive?: boolean
  isFetchingMoreItems: boolean
  hasLoadingItems: boolean
  hasObservedItemTargetsSet: boolean
  isFirstHoriPagCall: boolean
  intersectionObserver?: IntersectionObserver
  constructor(props: FacetedCarouselProps) {
    super(props)
    this.isHoriPagActive = props.featureSwitches &&
      (props.featureSwitches.horiPageInitPreload || props.featureSwitches.horiPageWithoutInitPreload) || false
    this.state = {
      items: props && props.items,
      isEditActive: false,
      isDirty: false,
      truncateItemList: this.isHoriPagActive ? false : Boolean(props.featureSwitches && props.featureSwitches.delayItemsRendering)
    }
    this.isGettingEnriched = false
    this.isPartiallyEnriched = false
    this.isFullyEnriched = false

    this.toggleEdit = this.toggleEdit.bind(this)
    this.carouselScrollInteractionHandler = this.carouselScrollInteractionHandler.bind(this)
    this.carouselNavClickInteractionHandler = this.carouselNavClickInteractionHandler.bind(this)
    this.carouselInteractionHandler = this.carouselInteractionHandler.bind(this)
    this.containerRef = React.createRef()

    this.isHoriPageInitPreloadActive = props.featureSwitches && props.featureSwitches.horiPageInitPreload
    this.isFetchingMoreItems = false
    this.hasLoadingItems = false
    this.hasObservedItemTargetsSet = false
    this.isFirstHoriPagCall = true
  }

  componentDidCatch(error: Error) {
    csm.count('DVWeb::Storefront::JSFatal::FacetedCarousel')
    csm.logError(error, '[DVWeb::Storefront] Error while trying to render a FacetedCarousel')
  }

  toggleEdit() {
    this.setState({ isEditActive: !this.state.isEditActive })
  }

  setCollectionDirty() {
    if (!this.state.isDirty) {
      this.setState({ isDirty: true, truncateItemList: false })
    }
  }

  carouselScrollInteractionHandler() {
    csm.count('CarouselScroll')
    this.carouselInteractionHandler()
  }

  carouselNavClickInteractionHandler() {
    csm.count('CarouselPagination')
    this.carouselInteractionHandler()
  }

  componentDidUpdate() {
    // If the carousel contains the focused element, but
    // another has more recently been interacted with, clear
    // the focus, to close the hover (if it's open)
    if (!this.props.collectionHasFocusForHover) {
      const activeElement = document.activeElement as HTMLElement
      const currentContainer = this.containerRef && this.containerRef.current
      if (currentContainer && activeElement && currentContainer.contains(activeElement))
        activeElement.blur()
    }
    if (this.props.filterOn)
      this.setCollectionDirty()

    if (this.isHoriPagActive) {
      if (!this.hasLoadingItems) this.addLoadingItems()
      else this.ensureTargetsAreObserved()
    }
  }

  addLoadingItems() {
    this.hasLoadingItems = true
    const newItems = [...this.props.items, ...this.createLoadingItems(this.props.isDoubleDeckerCarouselType ?
      DOUBLE_DECKER_CAROUSEL_THRESHOLD : CAROUSEL_THRESHOLD)]

    this.props.updateCollection(this.props.collectionKey, newItems)
  }

  ensureTargetsAreObserved() {
    const intersectionObserver = this.intersectionObserver
    if (!intersectionObserver || !this.containerRef.current)
      return
    if (!this.hasObservedItemTargetsSet && this.hasLoadingItems) {
      const items = this.props.items
      const carouselItemContainers = this.containerRef.current.querySelectorAll('li > *')
      const numOfLoadingCards = this.props.isDoubleDeckerCarouselType ?
        DOUBLE_DECKER_CAROUSEL_THRESHOLD : CAROUSEL_THRESHOLD

      for (let i = carouselItemContainers.length - 1; i >= 0 && !this.hasObservedItemTargetsSet; i--) {
        if (items[i].itemType === "Loading") {
          intersectionObserver.observe(carouselItemContainers[i])
        } else if (!this.isFirstHoriPagCall && (i === 0 || i === (carouselItemContainers.length - 1 -
          numOfLoadingCards * PRELOADING_LOADING_CARDS_MULTIPLE))) {
          intersectionObserver.observe(carouselItemContainers[i])
          this.hasObservedItemTargetsSet = true
        } else if (this.isFirstHoriPagCall) {
          this.hasObservedItemTargetsSet = true
        }
      }
    }
  }

  fetchMoreItems() {
    this.isFetchingMoreItems = true

    //TODO kick off call to pageinateCollection WPS endpoint to retrieve real data for next set of cards
    /* 
    setTimeout(() => {
      this.intersectionObserver.disconnect()
      this.hasObservedItemTargetsSet = false
    
      if(this.isFirstHoriPagCall) this.isFirstHoriPagCall = false

      const mainItems = this.props.items.filter(item => item.itemType !== "Loading")
      this.hasLoadingItems = false
      this.isFetchingMoreItems = false
      const newItems = [...mainItems, ...mainItems.slice(0,20).map((item, i) => { return {...item, title: item.title + i +
              Math.floor(Math.random() * 100000)}})]
      this.props.updateCollection(this.props.collectionKey, newItems)

    }, 1000)*/
  }

  componentDidMount() {
    if (this.isHoriPagActive) {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (!this.isFetchingMoreItems) this.fetchMoreItems()
        }
      }, { threshold: 0.25 })
      if (this.isHoriPageInitPreloadActive && this.containerRef.current) this.intersectionObserver.observe(this.containerRef.current)
      else this.addLoadingItems()
    }
  }

  carouselInteractionHandler() {
    if (!this.props.canLazyLoad || this.isGettingEnriched) {
      this.setCollectionDirty()
    }
  }

  createCard = (item: StorefrontStateItem, i: number, lazyThreshold: number, inView: boolean) => {
    const isFilteredOut = this.props.filterOn && !safeBool(item.filterEntitled)
      const isAfterThreshold = i > lazyThreshold
      return (<Card
        key={`${item.title} ${i}`}
        {...item}
        collectionType={this.props.collectionType}
        isTwinHero={this.props.isTwinHeroCarouselType}
        isSquare={this.props.isSquareCarouselType}
        isPortrait={this.props.isPortraitCarouselType}
        isDoubleDecker={this.props.isDoubleDeckerCarouselType}
        isBoxArt={this.props.isBoxArtCarouselType}
        isEditActive={this.state.isEditActive}
        removeItem={(removeFromWatchNext?: boolean) => this.props.removeItem(i, this.props.collectionKey, removeFromWatchNext)}
        triggerCarouselLazyload={this.carouselInteractionHandler}
        isLazy={!this.isHoriPagActive && !this.state.isDirty && isAfterThreshold}
        hoverClass={this.props.hoverClass}
        featureSwitches={this.props.featureSwitches}
        description={item.title}
        isRemoved={isFilteredOut || item.isRemoved}
        pos={i}
        canLivePlayback={this.props.canLivePlayback}
        showProgressBar={Boolean(this.props.edit)}
        isVerticallyVisible={inView}
        isIsolatedCarousel={this.props.isIsolatedCarousel}
        showPlayButton={this.props.showPlayButton}
        isPhablet={this.props.isPhablet}
        showPrerollsInHover={safeBool(this.props.showPrerollsInHover)}
        takeFocusForCarousel={this.props.requestFocusForHover}
        resetFocusForCarousel={this.props.resetFocusForHover}
        isHoverDisabled={this.state.isEditActive}
        isOnLastCarousel={this.props.isLast}
      />)
  }

  createLoadingItems(numberOfCards: number) {
    const LOADING_CARD = {
      itemType: "Loading",
      entitlements: [] as string[],
      properties: {}
    } as StorefrontStateItem

    const cards = []

    for (let i = 0; i < numberOfCards; i++) {
      let clone = Object.assign({}, LOADING_CARD)
      clone.title = "Loading" + i
      cards.push(clone)
    }

    return cards
  }

  getCarouselItems(items: StorefrontStateItem[], inView: boolean) {
    const LAZY_THRESHOLD = 8
    const SQUARE_LAZY_THRESHOLD = 14
    const FACTOR = this.props.isDoubleDeckerCarouselType ? 2 : 1
    // The portrait carousel currently has the same "weight" as the square carousel
    const thisCarouselLazyThreshold = ((this.props.isSquareCarouselType || this.props.isPortraitCarouselType) ? SQUARE_LAZY_THRESHOLD : LAZY_THRESHOLD) * FACTOR
    const truncatedItems = this.state.truncateItemList ? items.slice(0, thisCarouselLazyThreshold + 1) : items

    if (this.props.isDoubleDeckerCarouselType) {
      const pairedTruncatedItems: StorefrontStateItem[][] = truncatedItems.reduce((result, _, index, array) => {
        if (index % 2 === 0)
          result.push(array.slice(index, index + 2));
        return result;
      }, [])

      const pairedCarouselItems = pairedTruncatedItems.map((pair, i) => {
        return (
          <li key={pair.map(_ => _.title).join()} className={cardStyle.DoubleCardWrapper}>
            {pair.map((item) => this.createCard(item, i, thisCarouselLazyThreshold, inView))
            }
          </li>
        )
      })

      return pairedCarouselItems
    } else {
      const carouselItems = truncatedItems.filter(item => item).map((item, i) => this.createCard(item, i, thisCarouselLazyThreshold, inView))

      return carouselItems
    }
  }

  render() {
    const props = this.props
    const items = props.items

    const onCarouselScroll = this.isHoriPagActive ? undefined : this.carouselScrollInteractionHandler
    const onMoveTo = this.isHoriPagActive ? undefined : this.carouselNavClickInteractionHandler

    return (
      <div
        ref={this.containerRef}
        onFocus={() => { this.props.requestFocusForHover && this.props.requestFocusForHover() }}
        className={cn(style.FacetedCarousel, 'tst-faceted-carousel', {
          'tst-editable-carousel': Boolean(this.props.edit),
          ['mobile-collection']: props.isPhablet,
          ['min-width-collection']: props.featureSwitches &&
            props.featureSwitches.requiresMinimumPageWidth,
          [style.isolatedCarousel]: this.props.isIsolatedCarousel,
          [style.overflowVisible]: this.props.featureSwitches.hover2019
        })}
      >
        <Header
          {...props}
          toggleEdit={this.toggleEdit}
          isEditActive={this.state.isEditActive}
          noMarginLeft={this.props.isIsolatedCarousel}
          seeMoreDescription={this.props.seeMoreDescription}
        />
        {
          <InView triggerOnce={true}>{(inview) =>
            <div
              onScroll={onCarouselScroll}>
              <Carousel
                unusableVisibleWidth={parseInt(style.unusedCarouselSpace, 10)}
                moveToListener={onMoveTo}
                wrapChildren={false}
                className={cn({
                  [style.focusControl]: !this.state.isEditActive && this.props.collectionHasFocusForHover,
                  [style.isLast]: this.props.isLast
                })}
                leftScrollButtonClassName={style.scrollButton}
                rightScrollButtonClassName={style.scrollButton}
              >
                {this.getCarouselItems(items, inview)}
              </Carousel>
            </div>}
          </InView>
        }
      </div>
    )
  }
}

export default FacetedCarousel
